'use client';

import type { DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeaderBase as DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeaderBase.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import React, { lazy, Suspense, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@packages/shared/src/ui/Tabs/Tabs';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { CustomerNrLoginForm } from '../CustomerNrLoginForm/CustomerNrLoginForm.client';
import { messages } from './messages';

const LoginForm = lazy(
  () => import(/* webpackChunkName: 'LoginForm' */ '../LoginForm/LoginForm.client'),
);
const ChangeAddressDialog = lazy(
  () =>
    import(
      /* webpackChunkName: 'ChangeAddressDialog' */ '../PostLoginChecks/ChangeAddressDialog/ChangeAddressDialog'
    ),
);

export interface LoginDialogProps extends Omit<DialogProps, 'onClose'> {
  /** required to close dialog after successful login */
  handleClose: () => void;
}

/** Dialog with user login form in two modes: 'e-mail & password login' or 'customer number & birthday login' */
export const LoginDialog = ({ handleClose, ...props }: LoginDialogProps) => {
  const { formatMessage } = useIntl();
  const { enableCustomerNrLogin } = useConfig();
  const [showChangeAddress, setShowChangeAddress] = useState<{
    firstName: string;
    lastName: string;
    countryCode: string;
  } | null>(null);

  const handleShowChangeAddress = (firstName: string, lastName: string, countryCode: string) => {
    if (!showChangeAddress) {
      setShowChangeAddress({ firstName, lastName, countryCode });
    }
  };

  if (showChangeAddress) {
    return (
      <Suspense>
        <ChangeAddressDialog
          firstName={showChangeAddress.firstName}
          lastName={showChangeAddress.lastName}
          countryCode={showChangeAddress.countryCode}
        />
      </Suspense>
    );
  }

  return (
    <Dialog
      {...props}
      className="lg:w-3xl"
      onClose={handleClose}
      title={formatMessage(messages.modalHeadline)}
    >
      <DialogHeader onClose={handleClose}>{formatMessage(messages.modalHeadline)}</DialogHeader>
      <DialogContent className="p-0 whitespace-pre-line">
        {enableCustomerNrLogin ? (
          <Tabs className="border-color-grey w-full" defaultValue="email">
            <TabsList className="bg-color-background-base w-full">
              <TabsTrigger
                className="text-size-body-sm text-color-text-dark flex-1 basis-auto text-wrap whitespace-normal data-[state=active]:font-bold"
                value="email"
              >
                {formatMessage(messages.emailPasswordTabTitle)}
              </TabsTrigger>
              <TabsTrigger
                className="text-size-body-sm text-color-text-dark flex-1 basis-auto text-wrap whitespace-normal data-[state=active]:font-bold"
                value="customerNo"
              >
                {formatMessage(messages.customerNoTabTitle)}
              </TabsTrigger>
            </TabsList>
            <TabsContent className="bg-color-grey-light mt-0" value="email">
              <Suspense>
                <LoginForm showChangeAddress={handleShowChangeAddress} onSuccess={handleClose} />
              </Suspense>
            </TabsContent>
            <TabsContent className="bg-color-grey-light mt-0 p-2" value="customerNo">
              <CustomerNrLoginForm />
            </TabsContent>
          </Tabs>
        ) : (
          <Suspense>
            <LoginForm showChangeAddress={handleShowChangeAddress} onSuccess={handleClose} />
          </Suspense>
        )}
      </DialogContent>
    </Dialog>
  );
};
